import React from 'react';
import Icon from '../../../components/AppIcon';

const TaskSummary = ({ tasks, persons }) => {
  const calculateStats = () => {
    const stats = persons?.map(person => {
      const personTasks = tasks?.filter(task => task?.personId === person?.id);
      const completed = personTasks?.filter(task => task?.completed)?.length;
      const total = personTasks?.length;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        ...person,
        completed,
        total,
        percentage,
        pending: total - completed
      };
    });

    return stats;
  };

  const stats = calculateStats();
  const totalTasks = tasks?.length;
  const completedTasks = tasks?.filter(task => task?.completed)?.length;
  const overallPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-5 lg:p-6">
      <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
        <Icon name="bar-chart-3" size={20} className="text-primary" />
        <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          Daily Summary
        </h2>
      </div>
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-4 md:p-5 mb-4 md:mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm md:text-base text-foreground font-medium">Overall Progress</span>
          <span className="text-xl md:text-2xl lg:text-3xl font-bold text-primary">
            {overallPercentage}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 md:h-3 mb-2">
          <div 
            className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-500"
            style={{ width: `${overallPercentage}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs md:text-sm text-muted-foreground">
          <span>{completedTasks} completed</span>
          <span>{totalTasks - completedTasks} pending</span>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        {stats?.map((stat) => (
          <div 
            key={stat?.id}
            className="bg-background rounded-lg p-3 md:p-4 border-l-4 transition-smooth hover:shadow-warm-sm"
            style={{ borderLeftColor: stat?.color }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center text-white text-xs md:text-sm font-semibold"
                  style={{ backgroundColor: stat?.color }}
                >
                  {stat?.initials}
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-medium text-foreground">
                    {stat?.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">{stat?.role}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg md:text-xl font-bold" style={{ color: stat?.color }}>
                  {stat?.percentage}%
                </div>
                <div className="text-xs text-muted-foreground">
                  {stat?.completed}/{stat?.total}
                </div>
              </div>
            </div>

            <div className="w-full bg-muted rounded-full h-1.5 md:h-2">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${stat?.percentage}%`,
                  backgroundColor: stat?.color
                }}
              />
            </div>

            <div className="flex items-center gap-3 md:gap-4 mt-2 text-xs md:text-sm">
              <div className="flex items-center gap-1 text-success">
                <Icon name="check-circle" size={14} />
                <span>{stat?.completed} done</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon name="circle" size={14} />
                <span>{stat?.pending} pending</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-success/10 rounded-lg p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="check-circle-2" size={16} className="text-success" />
              <span className="text-xs md:text-sm text-muted-foreground">Completed</span>
            </div>
            <div className="text-xl md:text-2xl font-bold text-success">
              {completedTasks}
            </div>
          </div>

          <div className="bg-warning/10 rounded-lg p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="clock" size={16} className="text-warning" />
              <span className="text-xs md:text-sm text-muted-foreground">Pending</span>
            </div>
            <div className="text-xl md:text-2xl font-bold text-warning">
              {totalTasks - completedTasks}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskSummary;